import React from 'react';
import AnalysisPageLayout from '../../components/aesthetic/AnalysisPageLayout';
import ImageUpload from '../../components/aesthetic/ImageUpload';
import ProcedureSelector from '../../components/aesthetic/ProcedureSelector';
import IntensitySlider from '../../components/aesthetic/IntensitySlider';
import BeforeAfterComparison from '../../components/aesthetic/BeforeAfterComparison';
import NoseAnalysisResults from '../../components/aesthetic/NoseAnalysisResults';
import LoadingOverlay from '../../components/aesthetic/LoadingOverlay';
import ErrorBanner from '../../components/aesthetic/ErrorBanner';
import EmptyState from '../../components/aesthetic/EmptyState';
import { useSimulator } from '../../hooks/useSimulator';
import { useInlineNoseAnalysis } from '../../hooks/useInlineNoseAnalysis';
import { PROCEDURES } from '../../constants/procedures';

function getProcedureLabel(procedureId) {
  return PROCEDURES.find((p) => p.id === procedureId)?.label || procedureId;
}

function CosmeticSimulator() {
  const {
    imagePreview,
    procedure,
    intensity,
    fieldErrors,
    error,
    loading,
    results,
    beforeImage,
    afterImage,
    handleImageChange,
    handleProcedureChange,
    handleIntensityChange,
    generatePreview,
    resetPreview,
  } = useSimulator();

  const selectedLabel = getProcedureLabel(results?.procedure || procedure);
  const resultIntensity = results?.intensity ?? intensity;
  const activeProcedure = results?.procedure || procedure;
  const isRhinoplasty = activeProcedure === 'rhinoplasty';
  const hasPreview = Boolean(results && afterImage);

  const {
    loading: noseLoading,
    error: noseError,
    results: noseResults,
  } = useInlineNoseAnalysis(beforeImage, afterImage, isRhinoplasty && hasPreview);

  return (
    <AnalysisPageLayout
      title="AI Cosmetic Surgery Simulator"
      subtitle="Upload a patient photo, choose a procedure, adjust intensity, and generate an AI preview."
    >
      {loading && <LoadingOverlay message="Generating cosmetic surgery preview…" />}

      <section className="aesthetic-section">
        <h2 className="aesthetic-section-title">Patient Image</h2>
        <ErrorBanner message={error} />
        <div className="simulator-upload-row">
          <ImageUpload
            label="Upload Patient Photo"
            preview={imagePreview}
            onChange={handleImageChange}
            error={fieldErrors.image}
          />
        </div>
      </section>

      <section className="aesthetic-section">
        <h2 className="aesthetic-section-title">Procedure & Intensity</h2>
        <ProcedureSelector
          value={procedure}
          onChange={handleProcedureChange}
          error={fieldErrors.procedure}
        />
        <div className="simulator-intensity-row">
          <IntensitySlider value={intensity} onChange={handleIntensityChange} />
        </div>
        <div className="upload-form-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={generatePreview}
            disabled={loading}
          >
            Generate Preview
          </button>
          {results && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={resetPreview}
              disabled={loading}
            >
              Clear Preview
            </button>
          )}
        </div>
      </section>

      {!results && !loading && (
        <section className="aesthetic-section">
          <EmptyState
            icon="✨"
            title="No preview generated yet"
            description="Upload a patient photo, select a cosmetic procedure, adjust the intensity slider, then click Generate Preview to see the simulated result."
          />
        </section>
      )}

      {hasPreview && (
        <>
          <section className="aesthetic-section">
            <h2 className="aesthetic-section-title">Simulation Result</h2>
            <div className="simulator-result-meta">
              <span><strong>Procedure:</strong> {selectedLabel}</span>
              <span><strong>Intensity:</strong> {resultIntensity}%</span>
            </div>
            <div className="visualization-grid">
              {beforeImage && (
                <div className="visualization-card">
                  <div className="visualization-card-header">Before</div>
                  <div className="visualization-card-body">
                    <img src={beforeImage} alt="Before simulation" />
                  </div>
                </div>
              )}
              <div className="visualization-card">
                <div className="visualization-card-header">After (AI Preview)</div>
                <div className="visualization-card-body">
                  <img src={afterImage} alt={`${selectedLabel} simulation preview`} />
                </div>
              </div>
            </div>
          </section>

          <section className="aesthetic-section">
            <h2 className="aesthetic-section-title">Before / After Comparison</h2>
            <BeforeAfterComparison
              beforeImage={beforeImage}
              afterImage={afterImage}
              beforeLabel="Before"
              afterLabel="After"
            />
          </section>

          {isRhinoplasty && (
            <section className="aesthetic-section simulator-nose-results">
              <h2 className="aesthetic-section-title">Nose Analysis</h2>
              <p className="simulator-nose-cta-text">
                Clinical measurements calculated automatically from your rhinoplasty before and after preview.
              </p>
              <NoseAnalysisResults
                results={noseResults}
                loading={noseLoading}
                error={noseError}
              />
            </section>
          )}
        </>
      )}
    </AnalysisPageLayout>
  );
}

export default CosmeticSimulator;
